import * as React from "react";
import { DataGridPremium, GridRenderCellParams, GridRowId, useGridApiRef } from '@mui/x-data-grid-premium';
import CclDataGridToolbar from "../../../components/common/cclDataGridToolbar/cclDataGridToolbar";
import CreateListPanel from "./createListPanel";
import CopyListPanel from "./copyListPanel";
import { Alert, Stack } from "@mui/material";
import { useDeleteListMutation, useGetCurrentUserListsQuery } from "../../../services/cclTokenedGroupReportsApi";
import { UserClaimsService } from "../../../services/currentUserService/currentUserService";
import CclCommonLink from "../../../components/common/cclCommonLink";
import useLogAccessEvent from "../../../hooks/useLogAccessEvent";
import { AccessEventListDetails } from "../../../services/types/accessEventTypes";
import CclAlertSnackbar from "../../../components/common/cclAlertSnackbar";

export type MyListScreenProps = {
};

export const MyListScreen: React.FC = (props) => {
    
    const apiRef = useGridApiRef();

    const claimservice = new UserClaimsService();

    let email = claimservice.GetCurrentUserEmail();

    const { logEvent } = useLogAccessEvent();

    const { data: userLists } = useGetCurrentUserListsQuery(email.split("@")[0].toUpperCase());

    const [deleteList] = useDeleteListMutation();

    const [selectedRows, setSelectedRows] = React.useState<any[]>([]);
    const [openCreateList, setOpenCreateList] = React.useState<boolean>(false);
    const [openCopyList, setOpenCopyList] = React.useState<boolean>(false);

    const [openSnackbar, setOpenSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<"error" | "warning" | "info" | "success" | undefined>(undefined);

    const updateSelectedRows = (rows: any[] | undefined) => {
        if (!rows) return;
        let rowsMap: Map<GridRowId, any> = apiRef.current.getSelectedRows();
        let rowArray: any[] = [];
        for (let row of Array.from(rowsMap.values())) {
            rowArray.push(row);
        }
        setSelectedRows(rowArray);
    };

    const COLUMNS = [
        {
            field: "upL_ID",
            headerName: "ID",
            type: "string",
            flex: 0.1,
            renderCell: (params: GridRenderCellParams) => (
                <CclCommonLink
                    text={params.value}
                    keyVal={params.value}
                    to={`/assessments/group-reports/${params.row.upL_ID}`}
                />
            ),
        },
        {
            field: "participantListName",
            headerName: "Name",
            type: "string",
            flex: 0.5,
        },
        {
            field: "createdByDateTime",
            headerName: "Created",
            type: "string",
            flex: 1,
        },
    ];

    const deleteSelectedList = () => {
        let listIds = selectedRows.map((list) => {
            return list.upL_ID;
        });
        deleteList(listIds)
            .unwrap()
            .then(() => {
                const eventDetails: AccessEventListDetails = {
                    listName: selectedRows[0].participantListName,
                }
                logEvent("GroupReportListDeleted", eventDetails);

                handleSnackbar("success", "List deleted");
            })
            .catch((error) => {
                console.error("Failed to delete the list: ", error);
                handleSnackbar("error", "List deletion failed");
            });
    };

    const handleSnackbar = (severity: string, message: string) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity as "error" | "warning" | "info" | "success" | undefined);
        setOpenSnackbar(true);
    };

    const miscButtonClick = (id: string) => {
        switch (id) {
            case "create-list-button":
                setOpenCreateList(true);
                break;
            case "copy-list-button":
                setOpenCopyList(true);
                break;
            case "delete-list-button":
                deleteSelectedList();
                break;
            default:
                break;
        }
    };

    const handleCreateList = () => {
        setOpenCreateList(false);
    }

    const handleCopyList = () => {
        setOpenCopyList(false);
    }

    return (
        <>
            {openCreateList && 
                <CreateListPanel 
                    open={openCreateList} 
                    onClose={() => setOpenCreateList(false)} 
                    onCreateList={() => handleCreateList()}
                    snackbarHandler={handleSnackbar}                
                />
            }

            {openCopyList && 
                <CopyListPanel
                    open={openCopyList}
                    selectedRows={selectedRows[0]}
                    onClose={() => setOpenCopyList(false)}
                    onCopyList={() => handleCopyList()}
                    snackbarHandler={handleSnackbar}
                />   
            }

            <CclAlertSnackbar 
                open={openSnackbar} 
                message={snackbarMessage} 
                severity={snackbarSeverity} 
                onClose={() => setOpenSnackbar(false)} 
            />
            
            <Stack
                direction={"column"}
                spacing={2}
                width={"100%"}
            >
                <Alert
                    variant="outlined"
                    severity="info"
                >
                    This is a prototype replacement for the Group Reports Application for user testing purposes only. Please continue to use the Group Reports Application
                </Alert>

                <DataGridPremium
                    columns={COLUMNS}
                    rows={userLists ?? []}
                    apiRef={apiRef}
                    getRowId= {(row) => row.upL_ID}
                    components={{ Toolbar: CclDataGridToolbar }}
                    componentsProps={{
                        toolbar: {
                            showSaveState: false,
                            hideSettingsButtons: true,
                            savestate: null,
                            clearstate: null,
                            miscButtons:
                                [
                                    {
                                        id: "create-list-button",
                                        caption: "Create List",
                                        onClick: () => { },
                                        returnSelected: false,
                                        restricted: true,
                                    },
                                    {
                                        id: "copy-list-button",
                                        caption: "Copy List",
                                        onClick: () => { },
                                        returnSelected: true,
                                        restricted: true,
                                        disable:
                                            selectedRows === undefined ||
                                            selectedRows.length > 1 ||
                                            selectedRows.length === 0,
                                    },
                                    {
                                        id: "delete-list-button",
                                        caption: "Delete List",
                                        onClick: () => { },
                                        returnSelected: true,
                                        restricted: true,
                                        disable:
                                            selectedRows === undefined ||
                                            selectedRows.length > 1 ||
                                            selectedRows.length === 0,
                                    }
                                ],
                            btnClick: miscButtonClick,
                            anySelected: null,
                        },
                    }}
                    checkboxSelection
                    disableSelectionOnClick={false}
                    onSelectionModelChange={(rows: any[] | undefined) => updateSelectedRows(rows)}
                />
                
            </Stack>
        </>
    );
};

export default MyListScreen;
